import {parsePx, resolveContainerTemplate, setHideFlag, updateChildren, updateHidden, updateParent} from "../utils/ElementUtils.ts";
import {sanitizeHtml, sprintf, TEXT_MARKUP_TAGS} from "../utils/FormatUtils.ts";
import {Rule} from "../rule/Rule.ts";
import useStore from "../store.ts";
import {postUpdateGeometry} from "../worker/RenderWorkerManager.ts";
import {Subscription} from "./Subscription.ts";
import {DataField} from "./DynamicDataParser.ts";
import updateCoordinator from "../utils/UpdateCoordinator";
import {getSubscriptionManager} from "./SubscriptionManager.ts";
import {renderInvalidationRegistry, RenderInvalidationReason} from "../utils/RenderInvalidation.ts";
import {UpdateOrigin, updateContextRegistry, type UpdateContextHandle} from "../utils/UpdateContextRegistry.ts";
import {toRaw} from "vue";

const functionPropWarnings = new Set<string>();

/* see docs/EXTRACTED_COMMENTS.md#c013 */
export function clearFunctionPropWarnings(): void {
    functionPropWarnings.clear();
}

export class WrapperSubscription implements Subscription {
    private readonly id: string;
    public lastMatchedRow: number | null = null;
    public lastMatchedCh: number | null = null;
    private lastVersion = -1;
    private requestSeq = 0;
    private pendingAdoptReplay = false;
    private pendingAdoptReplayHandle: UpdateContextHandle | undefined = undefined;
    public rule: Rule | null;
    private lastContainerTemplate = "";

    public constructor(id: string) {
        this.id = id;
        this.rule = null;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c779 */
    private static readonly FULL_REPAINT_ORIGINS: ReadonlySet<UpdateOrigin> = new Set([
        UpdateOrigin.Initial,
        UpdateOrigin.Reconnect,
        UpdateOrigin.ChannelSync,
        UpdateOrigin.LayoutRebuild,
    ]);

    /** Field-value equality of two resolved rows (Map<field, DataField>); null equals null only. */
    private static rowDataEqual(a: any, b: any): boolean {
        if (a === b) return true;
        if (!(a instanceof Map) || !(b instanceof Map)) return false;
        if (a.size !== b.size) return false;
        for (const [key, av] of a) {
            if (!b.has(key)) return false;
            const bv = b.get(key);
            if (av === bv) continue;
            if (av == null || bv == null) return false;
            if (av.value !== bv.value || av.type !== bv.type) return false;
        }
        return true;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c014 */
    public get componentId(): string {
        return this.id;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c015 */
    public dispose(): void {
        if (this.rule) {
            this.rule.removeCallbacksForOwner(this.id);
            this.rule = null;
        }
        this.pendingAdoptReplay = false;
        this.pendingAdoptReplayHandle = undefined;
        this.requestSeq++;
    }

    public load() {
        const store = useStore();

        if (!store.components[this.id]) {
            throw new Error("Component must be defined before subscription.");
        }
        store.components[this.id].subscription = this;
        const item = store.components[this.id];

        if (item.decision_props?.rule != null) {
            setHideFlag(this.id, "rule", true, "decision_init");
        }

        // see docs/EXTRACTED_COMMENTS.md#c810
        if (item.container_props?.rule_maps?.length) {
            this.ensureContainerRules();
            this.resolveContainerRules();
        }

        if (!item.dynamic) return;

        if (item.dynamic.dynamic_channel) {
            store.channelManager.addChannel(item.props.id, item.dynamic.dynamic_channel, this);
        }
        if (item.dynamic.dynamic_key_channel) {
            store.channelManager.addChannel(item.props.id, item.dynamic.dynamic_key_channel, this);
        }
        if (item.dynamic.key_channel) {
            store.channelManager.addChannel(item.props.id, item.dynamic.key_channel, this);
        }
        if (item.dynamic.channel) {
            store.channelManager.addChannel(item.props.id, item.dynamic.channel, this);
        }

        if (item.function_props && item.function_props.second_channel != null) {
            store.channelManager.addChannel(item.props.id, item.function_props.second_channel, this);
        }

        if (item.dynamic.dynamic_fields) {
            for (const dynamic_field of item.dynamic.dynamic_fields) {
                if (!dynamic_field.adopt_object) continue;
                const adoptions = store.adoptions.get(dynamic_field.adopt_object.adopt_object_id) || [];
                if (adoptions.includes(this)) continue;
                store.adoptions.set(dynamic_field.adopt_object.adopt_object_id, [...adoptions, this]);
            }
        }
        if (item.dynamic.adopt_object) {
            const adoptions = store.adoptions.get(item.dynamic.adopt_object.adopt_object_id) || [];
            if (!adoptions.includes(this)) {
                store.adoptions.set(item.dynamic.adopt_object.adopt_object_id, [...adoptions, this]);
            }
        }
    }

    public async handleSubscription(
        data: any,
        row: number,
        channel: number,
        found: boolean,
        version: number,
        handle?: UpdateContextHandle,
        bypassVersionGuard: boolean = false
    ) {
        const inContext = (handle?.active) || updateCoordinator.isInContext(this.id);
        let contextCompletionIssued = false;
        const completeContextUpdate = () => {
            if (!inContext || contextCompletionIssued) return;
            contextCompletionIssued = true;
            if (handle?.active) {
                handle.complete(this.id);
            } else if (handle) {
                // see docs/EXTRACTED_COMMENTS.md#c016
                updateCoordinator.completeUpdate(this.id, handle.id);
            }
            // see docs/EXTRACTED_COMMENTS.md#c017
        };
        try {
            const store = useStore();
            const item = store.components[this.id];
            if (!item) {
                return;
            }
            // Pure reads go through the raw component: each read through the store's
            // reactive proxy costs far more, and this runs once per result. Writes and
            // the objects handed to other code stay on `item`, so reactivity is unchanged.
            const rawItem = toRaw(item);


            if (!bypassVersionGuard && version != 1 && version < this.lastVersion) {
                return;
            }
            const wasFound = rawItem.dynamic.found === true;
            const rowChanged = found && (this.lastMatchedRow !== row || this.lastMatchedCh !== channel);
            const matchStateChanged = found !== wasFound;
            const fullRepaint = handle != null && WrapperSubscription.FULL_REPAINT_ORIGINS.has(handle.origin);
            // A forced refresh re-cascades an unchanged match too: the cascade only runs on a change,
            // so a descendant that missed one would otherwise keep a stale row until the match moves.
            const dynamicRowCascaded = rawItem.dynamic?.is_dynamic === true
                && (rowChanged || matchStateChanged || (found && (bypassVersionGuard || fullRepaint)));

            // Recorded before the cascade awaits, so an overlapping result sees this one's match and
            // version instead of the previous ones.
            this.lastVersion = version;
            if (found) {
                this.lastMatchedRow = row;
                this.lastMatchedCh = channel;
            } else {
                this.lastMatchedRow = null;
                this.lastMatchedCh = null;
            }

            if (dynamicRowCascaded) {
                const newRow = found ? row : null;
                // Both dynamic channels, since the worker falls back from dynamic_key_channel to
                // dynamic_channel. The matched channel only on a match: a miss reports the parent's own
                // fixed-row channel, whose descendants this lookup does not drive.
                const propagationChannels = [found ? channel : null, rawItem.dynamic.dynamic_key_channel, rawItem.dynamic.dynamic_channel]
                    .filter((c): c is number => c != null);
                for (const child of rawItem.children ?? []) {
                    await updateChildren(child.props.id, newRow, propagationChannels, handle);
                }
            }

            item.dynamic.found = found;
            const oldData = item.data;

            if (this.rule === null && item.decision_props?.rule != undefined) {
                const channel = store.channelManager.getChannel(item.dynamic.key_channel)
                    || store.channelManager.getChannel(item.dynamic.channel);
                if (channel != null && item.dynamic.row != null) {
                    store!.socketManager!.requestRule(item.decision_props.rule, this.handleRuleCallback, item.dynamic.row ?? 0, channel.getName(), this.id)
                }
            }

            data = this.applyFunctionProps(item, data, oldData, row, channel, store);
            if (item.dynamic.adopt_object?.adopt_object_id && item.adoptData.has(item.dynamic.adopt_object.adopt_object_id)) {
                data = item.adoptData.get(item.dynamic.adopt_object.adopt_object_id).data ? new Map(Object.entries(item.adoptData.get(item.dynamic.adopt_object.adopt_object_id).data)
                    .map(([key, value]) => [!key ? null : Number(key), value])
                ) : null;
            }

            // see docs/EXTRACTED_COMMENTS.md#c779
            const dataChanged = !WrapperSubscription.rowDataEqual(oldData, data);

            let src = rawItem.published.src;
            // see docs/EXTRACTED_COMMENTS.md#c018
            let recomputed = false;
            const text = rawItem?.text_props?.format || rawItem?.image_props?.format || rawItem?.playlist_props?.format || rawItem?.html_props?.format;
            if (rawItem.type == "DataObjectCountdown" || rawItem.type == "DataObjectTimer") {
                // This is calculated at run-time. A timer additionally has no *_props.format at
                // all, so falling through would sprintf(undefined) — see #c777.
            }else if (rawItem.type == "DataObjectEditor") {
                // see docs/EXTRACTED_COMMENTS.md#c019
                const raw = (data != null && rawItem.dynamic?.field?.field)
                    ? data.get(rawItem.dynamic.field.field)?.value
                    : null;
                src = raw != null ? String(raw) : "";
                recomputed = true;
            }else if (item.dynamic.adopt_object?.adopt_object_id && item.adoptData.has(item.dynamic.adopt_object.adopt_object_id)) {
                let raw_data = item.dynamic.adopt_object.use_raw_data;
                let adopt_data = item.adoptData.get(item.dynamic.adopt_object.adopt_object_id);
                if (raw_data) {
                    src = sprintf(text, adopt_data.raw);
                } else if (text?.includes('%')) {
                    // Formatted adopt: the source's formatted value still goes through this object's own format.
                    src = sprintf(text, adopt_data.format);
                } else {
                    // No format of its own: the source's formatting is ignored, the raw value is shown.
                    src = adopt_data.raw != null ? String(adopt_data.raw) : "";
                }
                recomputed = true;
            } else if (data != null && rawItem.dynamic?.field?.field) {
                src = sprintf(text, data.get(rawItem.dynamic?.field?.field)?.value, rawItem.dynamic.field.type);
                recomputed = true;
            } else if (data != null) {
                src = sprintf(text, null, undefined);
                recomputed = true;
            } else if (rawItem.dynamic?.field && (text?.includes('%') || text?.includes(':'))) {
                src = '';
                recomputed = true;
            }

            // see docs/EXTRACTED_COMMENTS.md#c020
            if (recomputed && rawItem.text_props != null && typeof src === "string") {
                src = sanitizeHtml(src, TEXT_MARKUP_TAGS);
            }

            // see docs/EXTRACTED_COMMENTS.md#c779
            const srcChanged = src !== rawItem.published.src;
            const changed = fullRepaint || dataChanged || srcChanged || matchStateChanged || rowChanged;
            if (changed) {
                updateCoordinator.deferGeometryUpdate(this.id, item);
                updateCoordinator.deferSrcUpdate(this.id, item, src);
            }

            const adoptions = store.adoptions.get(this.id) || [];
            const value = data ? (data).get(rawItem.dynamic?.field?.field)?.value : null;
            for (const adopt of adoptions) {
                // see docs/EXTRACTED_COMMENTS.md#c021
                const adoptItem = store.components[(adopt as WrapperSubscription).componentId];
                if (!adoptItem?.subscription) continue;
                // Unchanged source: only an adoptee that never received it needs the drive (#c779).
                if (!changed && adoptItem.adoptData.has(this.id)) continue;
                await (adoptItem.subscription as WrapperSubscription).handleAdopts(this.id!, value, src, data, row, channel, handle);
            }

            if (changed && (dataChanged || matchStateChanged || fullRepaint || (!found && rawItem.auto_hide))) {
                updateCoordinator.deferHiddenUpdate(this.id, item, data);
            }

            const mutated = changed;

            if (row != null) {
                if (this.rule) {
                    const rule = this.rule;
                    // Re-evaluate this object's OWN bound row on the channel it reads — not the row that
                    // happened to arrive. Every later row of the same channel was overwriting the verdict,
                    // and so was this row while still half-merged (SocketListener.applyRowWrite merges
                    // partial field sets into an existing row). Whatever landed last stuck, because
                    // nothing re-evaluates once the row is complete.
                    updateCoordinator.deferRuleCallback(this.id, () => {
                        const current = store.components[this.id];
                        if (!current?.dynamic) return;
                        const ruleChannel = store.channelManager.getChannel(current.dynamic.key_channel)
                            || store.channelManager.getChannel(current.dynamic.channel);
                        if (!ruleChannel) return;
                        this.handleRuleCallback(rule, current.dynamic.row ?? 0, ruleChannel.getName());
                    });
                }

                if (rawItem.container_props?.rule_maps) {
                    this.ensureContainerRules();
                    updateCoordinator.deferRuleCallback(this.id, () => this.resolveContainerRules());
                }
            }
            // see docs/EXTRACTED_COMMENTS.md#c022
            if (mutated || dynamicRowCascaded) {
                const impactedIds = [
                    this.id,
                    ...(rawItem.children ?? []).map((c: any) => c.props?.id ?? c?.id).filter(Boolean)
                ];
                if (import.meta.env.DEV && handle?.active) {
                    const owning = updateCoordinator.getContextId(this.id);
                    if (owning && owning !== handle.id) {
                        console.warn(
                            `[WrapperSubscription] context divergence for ${this.id}: handle=${handle.id} owning=${owning}`
                        );
                    }
                }
                updateCoordinator.queueInvalidation(
                    this.id,
                    RenderInvalidationReason.DataMutation,
                    impactedIds
                );
            }

            if (this.pendingAdoptReplay) {
                const replayHandle = this.pendingAdoptReplayHandle;
                this.pendingAdoptReplay = false;
                this.pendingAdoptReplayHandle = undefined;
                await this.updateData("", -1, [], replayHandle?.active ? replayHandle : handle);
            }

        } finally {
            completeContextUpdate();
        }

    }

    private getFieldFromRow(rowData: any, fieldId?: number): DataField | undefined {
        if (fieldId == null || rowData == null) return undefined;
        if (rowData instanceof Map) return rowData.get(fieldId);
        if (Array.isArray(rowData)) {
            return rowData.find((d: any) => Number(d?.field) === fieldId);
        }
        if (typeof rowData === "object" && (rowData as any)[fieldId] != null) {
            const candidate = (rowData as any)[fieldId];
            if (candidate && typeof candidate === "object" && "field" in candidate) {
                return candidate as DataField;
            }
        }
        return undefined;
    }

    private parseNumber(value: any): number | null {
        if (value === null || value === undefined) return null;
        const num = typeof value === "number" ? value : Number.parseFloat(String(value));
        if (!Number.isFinite(num)) return null;
        return num;
    }

    private logFunctionPropFault(kind: string, message: string) {
        const key = `${this.id}:${kind}`;
        if (functionPropWarnings.has(key)) return;
        functionPropWarnings.add(key);
        console.warn(message);
    }

    private applyFunctionProps(item: any, incomingData: any, oldData: any, _row: number | null, _channel: number | null, store: ReturnType<typeof useStore>) {
        const fp = item.function_props;
        if (!fp || !item.dynamic) return incomingData;

        const data = new Map<number, any>(incomingData?.entries() ?? []);
        if (!data) return incomingData;

        // see docs/EXTRACTED_COMMENTS.md#c325
        const targetFieldId = item.dynamic?.field?.field;
        const targetFieldType = item.dynamic?.field?.type;
        if (targetFieldId == null) return incomingData;

        const primaryField = data.get(targetFieldId);
        let primaryRaw = primaryField?.value ?? fp.first_argument;
        if (targetFieldType == 3) {
            primaryRaw *= 1000;
        }

        const secondaryChannelId = fp.second_channel ?? item.dynamic.key_channel ?? item.dynamic.channel;
        const secondaryFieldId = fp.second_field;
        const secondaryRow = fp.second_row ?? 0;

        let secondaryRaw: any | undefined;
        if (secondaryChannelId != null && secondaryFieldId != null) {
            const secondaryChannel = store.channelManager.getChannel(secondaryChannelId);
            const rows = secondaryChannel?.getRows();
            const rowData = rows ? rows[secondaryRow] : null;
            const secondaryField = this.getFieldFromRow(rowData, secondaryFieldId);
            if (secondaryField?.value !== undefined && secondaryField?.value !== null && `${secondaryField.value}` !== "") {
                secondaryRaw = secondaryField.value;
            }
        }

        if ((secondaryRaw === undefined || secondaryRaw === null || `${secondaryRaw}` === "") && fp.second_argument !== undefined && fp.second_argument !== null && `${fp.second_argument}` !== "") {
            secondaryRaw = fp.second_argument;
            if (targetFieldType == 3) {
                secondaryRaw = parseInt(secondaryRaw) * 60 * 1000;
            }
        }

        if (secondaryRaw === undefined || secondaryRaw === null || `${secondaryRaw}` === "") {
            if (oldData instanceof Map && oldData.has(targetFieldId)) {
                data.set(targetFieldId, oldData.get(targetFieldId)!);
            }
            if (secondaryChannelId == null) {
                this.logFunctionPropFault("missing-channel", `[function_props] No secondary channel found for ${item.props?.id ?? this.id}`);
            }
            this.logFunctionPropFault("missing-secondary", `[function_props] Missing secondary operand for ${item.props?.id ?? this.id}`);
            return data;
        }

        const operator = fp.operator;
        let result: number | string | null = null;
        const primaryNum = this.parseNumber(primaryRaw);
        const secondaryNum = this.parseNumber(secondaryRaw);

        switch (operator) {
            case "ADD":
                if (primaryNum == null || secondaryNum == null) {
                    this.logFunctionPropFault("parse-failure", `[function_props] Unable to parse operands for ADD on ${item.props?.id ?? this.id}`);
                    return data;
                }
                result = primaryNum + secondaryNum;
                break;
            case "SUBTRACT":
                if (primaryNum == null || secondaryNum == null) {
                    this.logFunctionPropFault("parse-failure", `[function_props] Unable to parse operands for SUBTRACT on ${item.props?.id ?? this.id}`);
                    return data;
                }
                result = primaryNum - secondaryNum;
                break;
            case "MULTIPLY":
                if (primaryNum == null || secondaryNum == null) {
                    this.logFunctionPropFault("parse-failure", `[function_props] Unable to parse operands for MULTIPLY on ${item.props?.id ?? this.id}`);
                    return data;
                }
                result = primaryNum * secondaryNum;
                break;
            case "DIVIDE":
                if (primaryNum == null || secondaryNum == null) {
                    this.logFunctionPropFault("parse-failure", `[function_props] Unable to parse operands for DIVIDE on ${item.props?.id ?? this.id}`);
                    return data;
                }
                if (secondaryNum === 0) {
                    this.logFunctionPropFault("divide-by-zero", `[function_props] Divide by zero for ${item.props?.id ?? this.id}`);
                    return data;
                }
                result = primaryNum / secondaryNum;
                break;
            case "MODULO":
                if (primaryNum == null || secondaryNum == null) {
                    this.logFunctionPropFault("parse-failure", `[function_props] Unable to parse operands for MODULO on ${item.props?.id ?? this.id}`);
                    return data;
                }
                if (secondaryNum === 0) {
                    this.logFunctionPropFault("divide-by-zero", `[function_props] Modulo by zero for ${item.props?.id ?? this.id}`);
                    return data;
                }
                result = primaryNum % secondaryNum;
                break;
            case "TZOFFSET": {
                const tzOffset = secondaryNum ?? primaryNum;
                if (tzOffset == null) {
                    this.logFunctionPropFault("parse-failure", `[function_props] Unable to parse operands for TZOFFSET on ${item.props?.id ?? this.id}`);
                    return data;
                }
                result = tzOffset;
                break;
            }
            case "TZONE": {
                const tz = secondaryRaw ?? primaryRaw;
                if (tz === undefined || tz === null || `${tz}` === "") {
                    this.logFunctionPropFault("parse-failure", `[function_props] Missing timezone value for ${item.props?.id ?? this.id}`);
                    return data;
                }
                result = String(tz);
                break;
            }
            default:
                this.logFunctionPropFault("invalid-operator", `[function_props] Unsupported operator ${operator} for ${item.props?.id ?? this.id}`);
                return data;
        }

        const prevField = data.get(targetFieldId);
        const nextField: DataField = {
            field: targetFieldId,
            field_name: prevField?.field_name ?? fp.table_name ?? "",
            type: prevField?.type ?? (typeof result === "number" ? 1 : 2),
            value: result as any
        };
        data.set(targetFieldId, nextField);

        if (item.digital_clock_props && !item.digital_clock_props.time_zone) {
            if (operator === "TZONE" && typeof result === "string") {
                item.digital_clock_props.time_zone = result;
                item.digital_clock_props.time_zone_offset_minutes = undefined;
            } else if (operator === "TZOFFSET" && typeof result === "number") {
                item.digital_clock_props.time_zone_offset_minutes = result;
            }
        }

        return data;
    }


    public handleRuleCallback = (rule: Rule, row: number, channel: string) => {
        const store = useStore();
        const item = store.components[this.id];
        if (!item) return;
        const channelObj = store.channelManager.getChannel(item.dynamic.key_channel)
            || store.channelManager.getChannel(item.dynamic.channel);
        if (!channelObj) return;
        if (channelObj.getName() != channel) return;

        rule.evaluate(channelObj, row).then(async (result: boolean) => {
            await this.evaluateRule(rule, result);
        });
    }

    private handleContainerRuleCallback = () => this.resolveContainerRules();

    // see docs/EXTRACTED_COMMENTS.md#c810
    private ensureContainerRules(): void {
        const store = useStore();
        const item = store.components[this.id];
        const ruleMaps = item?.container_props?.rule_maps;
        if (!ruleMaps) return;
        const row = item.dynamic?.row ?? 0;
        const channel = String(item.dynamic?.key_channel ?? item.dynamic?.channel ?? "");
        for (const {rule: name} of ruleMaps) {
            if (!name) continue;
            const registered = store.ruleManager.getRule(name)?.getCallbacks()
                .some((c) => c.getCallback() === this.handleContainerRuleCallback) ?? false;
            if (!registered) {
                store.socketManager.requestRule(name, this.handleContainerRuleCallback, row, channel, this.id);
            }
        }
    }

    // see docs/EXTRACTED_COMMENTS.md#c810
    private resolveContainerRules(): void {
        const store = useStore();
        const item = store.components[this.id];
        const ruleMaps = item?.container_props?.rule_maps;
        if (!item || !ruleMaps) return;

        const channel = store.channelManager.getChannel(item.dynamic?.key_channel)
            || store.channelManager.getChannel(item.dynamic?.channel) || null;
        const results = ruleMaps.map((rm) => {
            const rule = store.ruleManager.getRule(rm.rule);
            let result = false;
            if (rule?.getValue() != undefined) {
                try {
                    result = rule.evaluateNow(channel, item.dynamic?.row!) === true;
                } catch (err) {
                    console.warn(`[WrapperSubscription] container rule ${rm.rule} failed for ${this.id}:`, err);
                }
            }
            return {...rm, result};
        });

        const selectedTemplate = resolveContainerTemplate(item, results);
        const suffixOf = (id?: string) => id?.split('.').pop();
        const selectedChild = selectedTemplate
            ? item.children.find(c => suffixOf(c?.props?.id) === selectedTemplate)
            : undefined;
        const element = selectedChild ? store.components[selectedChild.props.id] : undefined;

        // see docs/EXTRACTED_COMMENTS.md#c039
        if (!element) {
            this.lastContainerTemplate = "";
            return;
        }

        const width = parsePx(element?.style?.width) || 0;
        const height = parsePx(element?.style?.height) || 0;
        const parentWidth = parsePx(item?.props?.style?.width) || 0;
        const parentHeight = parsePx(item?.props?.style?.height) || 0;
        if (item.container_props.scale_template && (width > parentWidth || height > parentHeight)) {
            const innerScale = Math.min(parentWidth / width, parentHeight / height);
            if (innerScale > 0) {
                element.props.style.transformOrigin = 'left top';
                element.props.style.transform = `scale(${innerScale}, ${innerScale})`;
            }
        }

        const changed = this.lastContainerTemplate !== selectedTemplate;
        this.lastContainerTemplate = selectedTemplate;

        postUpdateGeometry(element.props.id, "container-template");

        // see docs/EXTRACTED_COMMENTS.md#c333
        if (changed) {
            renderInvalidationRegistry.requestRender(
                RenderInvalidationReason.ContainerTemplateChange,
                [item.props.id, ...item.children.map(c => c.props.id)]
            );
        }
    }

    public async evaluateRule(rule: Rule, result: boolean) {
        if (!rule) return;
        const store = useStore();
        const item = store.components[this.id];
        if (!item) return;
        if (item.decision_props?.rule == null) return;
        const display = item.decision_props.display ?? false;


        // see docs/EXTRACTED_COMMENTS.md#c326
        let currentStatus = store.components[this.id].hide_flags.rule;

        setHideFlag(this.id, "rule", display != result, "rule_evaluation");


        if (currentStatus != store.components[this.id].hide_flags.rule) {
            setHideFlag(this.id, "render", false, "rule_evaluation");

            // see docs/EXTRACTED_COMMENTS.md#c327
            updateHidden(this!.id);
            updateParent(this.id!);
            await postUpdateGeometry(this.id, "rule-evaluation");
            renderInvalidationRegistry.requestRender(
                RenderInvalidationReason.VisibilityChange,
                [this.id],
                { attachToActiveContext: false }
            );
        } else {
            // Rule hasn't changed, still update hidden state
            updateHidden(this!.id!);
        }

        if (this.rule == null) {
            this.rule = rule;
            const contextId = updateCoordinator.getContextId(this.id);
            const resolvedHandle = contextId ? updateContextRegistry.resolve(contextId) : undefined;
            await this.updateData("", -1, [], resolvedHandle ?? undefined);
        }
    }

    public async updateData(_channel = "", _row = -1, _dirty: DataField[] = [], handle?: UpdateContextHandle
    ) {
        const store = useStore();
        // Read-only here (see handleSubscription): the raw component skips the proxy cost.
        const item = toRaw(store.components)[this.id];
        if (!item) {
            return;
        }
        const debugForceRefresh = _channel === "" && _row === -1;

        let completedForHandle = false;
        const completeOnce = () => {
            if (!handle?.active || completedForHandle) return;
            completedForHandle = true;
            handle.complete(this.id);
        };
        const secondaryChannel = item.function_props != undefined
            ? (item.function_props.second_channel ?? item.dynamic.channel ?? item.dynamic.key_channel)
            : null;

        const secondaryRow = item.function_props ? (item.function_props.second_row ?? 0) : null;
        const isSecondaryChannelUpdate = item.function_props != undefined
            && secondaryChannel != null
            && `${secondaryChannel}` == _channel;
        const isSecondaryUpdate = isSecondaryChannelUpdate
            && secondaryRow != null
            && secondaryRow == _row;

        let currentSeq = this.requestSeq;
        if (_row != -1 && _channel.length != 0) {
            if (isSecondaryChannelUpdate && !isSecondaryUpdate) {
                return;
            }
            if (!item.dynamic.is_dynamic && _row != item.dynamic.row && !isSecondaryUpdate) {
                return;
            }
        }

        const dyn = item.dynamic;
        const channel = dyn?.key_channel ?? dyn?.channel;
        const row = dyn?.row;

        if (channel == null || row == null || row < 0) {
            if (handle?.active) {
                handle.register(this.id);
            }


            currentSeq = ++this.requestSeq;
            let result: any = null;
            try {
                result = await getSubscriptionManager().postDirect(item.props.id);
            } catch (err) {
                console.warn(`[WrapperSubscription] postTask failed for ${item.props.id}:`, err);
                completeOnce();
                return;
            }
            const {data: data, row: r, channel: ch, found, version} = result || {};

            // see docs/EXTRACTED_COMMENTS.md#c328
            const rowDrifted = (store.components[this.id]?.dynamic.row ?? row) !== row;
            if (currentSeq !== this.requestSeq && (!debugForceRefresh || rowDrifted)) {
                completeOnce();
                return;
            }

            try {
                await this.handleSubscription(
                    data, r, ch, found, version,
                    handle,
                    debugForceRefresh
                );
            } catch (error) {
                console.warn(`[WrapperSubscription] handleSubscription failed for ${item.props.id}:`, error);
                completeOnce();
            }
            return;
        }
        if (handle) {
            handle.register(this.id);
        }

        currentSeq = ++this.requestSeq;
        try {
            if (handle?.active) {
                void getSubscriptionManager().requestSubscriptionUpdate(
                    item.props.id,
                    channel,
                    row,
                    (data, r, ch, found, version, error) => {
                        if (error) {
                            console.warn(`[WrapperSubscription] Subscription callback failed for ${item.props.id}:`, error);
                            completeOnce();
                            return;
                        }
                        // see docs/EXTRACTED_COMMENTS.md#c329
                        const rowDrifted = (store.components[this.id]?.dynamic.row ?? row) !== row;
                        if (currentSeq !== this.requestSeq && (!debugForceRefresh || rowDrifted)) {
                            completeOnce();
                            return;
                        }
                        void this.handleSubscription(
                            data, r, ch, found, version,
                            handle,
                            debugForceRefresh
                        )
                            .catch((handleError) => {
                                console.warn(`[WrapperSubscription] handleSubscription failed for ${item.props.id}:`, handleError);
                                completeOnce();
                            });
                    },
                    handle.id
                ).catch((_e) => {
                    completeOnce();
                });
            } else {
                await getSubscriptionManager().requestSubscriptionUpdate(
                    item.props.id,
                    channel,
                    row,
                    (data, r, ch, found, version, error) => {
                        if (error) {
                            console.warn(`[WrapperSubscription] Subscription callback failed for ${item.props.id}:`, error);
                            return;
                        }
                        // see docs/EXTRACTED_COMMENTS.md#c330
                        const rowDrifted = (store.components[this.id]?.dynamic.row ?? row) !== row;
                        if (currentSeq !== this.requestSeq && (!debugForceRefresh || rowDrifted)) {
                            return;
                        }
                        void this.handleSubscription(
                            data, r, ch, found, version,
                            undefined,
                            debugForceRefresh
                        )
                            .catch((handleError) => {
                                console.warn(`[WrapperSubscription] handleSubscription failed for ${item.props.id}:`, handleError);
                            });
                    }
                );
            }
        } catch (err) {
            console.warn(`[WrapperSubscription] requestSubscriptionUpdate failed for ${item.props.id}:`, err);
            completeOnce();
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c331 */
    public async clearForCascade(handle?: UpdateContextHandle) {
        const store = useStore();
        const item = store.components[this.id];
        if (!item) return;
        this.requestSeq++;
        if (handle?.active) {
            handle.register(this.id);
        }
        await this.handleSubscription(null, null as any, null as any, false, this.lastVersion, handle, true);
    }


    public async handleAdopts(source: string, rawData: any, formattedData: string,
                        data: any, row: number | null, channel: number | null,
                        handle?: UpdateContextHandle) {
        const store = useStore();
        const item = store.components[this.id];
        if (!item) {
            return;
        }
        let dataObject = data != null ? Object.fromEntries(data.entries()) : null;
        item.adoptData.set(source, {raw: rawData, format: formattedData, data: dataObject, row, channel});
        const inInitialLoad = handle?.origin === UpdateOrigin.Initial
                           || handle?.origin === UpdateOrigin.DatasheetBuild;
        if (inInitialLoad) {
            this.pendingAdoptReplay = true;
            this.pendingAdoptReplayHandle = handle?.active ? handle : undefined;
        }
        await this.updateData("", -1, [], handle);
    }
}
